import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    FaArrowLeft, FaArrowRight, FaCheck, FaExclamationTriangle,
    FaCheckCircle, FaRegSquare, FaCheckSquare, FaSearch, FaTimes
} from 'react-icons/fa';
import { listServiceTypes } from '../services/serviceTypeService';
import { searchClients, applyServicePricingBulk } from '../services/clientService';
import ConfirmationModal from '../components/ConfirmationModal';

const GST_RATE = 0.1;

// ─── Step Indicator ───────────────────────────────────────────────────────────
const StepIndicator = ({ step, complete }) => {
    const steps = [
        { num: 1, label: 'Set Prices' },
        { num: 2, label: 'Select Suppliers' },
        { num: 3, label: 'Review & Apply' },
    ];
    return (
        <div className="flex items-center justify-center mb-8">
            {steps.map((s, i) => {
                const isDone = complete ? true : step > s.num;
                const isActive = !isDone && step === s.num;
                return (
                    <React.Fragment key={s.num}>
                        <div className="flex flex-col items-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors
                                ${isDone
                                    ? 'bg-jims-green border-jims-green text-white'
                                    : isActive
                                        ? 'border-jims-green text-jims-green bg-white'
                                        : 'border-gray-300 text-gray-400 bg-white'}`}>
                                {isDone ? <FaCheck size={12} /> : s.num}
                            </div>
                            <span className={`text-xs mt-1 font-medium whitespace-nowrap
                                ${isActive ? 'text-jims-green' : isDone ? 'text-gray-600' : 'text-gray-400'}`}>
                                {s.label}
                            </span>
                        </div>
                        {i < steps.length - 1 && (
                            <div className={`flex-1 h-0.5 mx-3 mb-5 transition-colors
                                ${isDone ? 'bg-jims-green' : 'bg-gray-200'}`} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const MassPriceUpdateUtility = ({ onBack }) => {
    const [step, setStep] = useState(1);

    // ── Step 1: Service type prices ──
    const [serviceTypes, setServiceTypes] = useState([]);
    const [loadingST, setLoadingST] = useState(true);
    const [pricing, setPricing] = useState({});          // { [id]: { pricePerHour: '', additionalPrice: '' } }
    const [selectedSTIds, setSelectedSTIds] = useState(new Set());
    const [quickFse, setQuickFse] = useState('');
    const [quickInvoice, setQuickInvoice] = useState('');

    // ── Step 2: Supplier selection ──
    const [supplierMode, setSupplierMode] = useState('all');  // 'all' | 'select'
    const [selectedClients, setSelectedClients] = useState([]);
    const [clientSearchTerm, setClientSearchTerm] = useState('');
    const [clientSuggestions, setClientSuggestions] = useState([]);
    const [isSearchingClients, setIsSearchingClients] = useState(false);

    // ── Step 3: Apply ──
    const [showConfirm, setShowConfirm] = useState(false);
    const [applying, setApplying] = useState(false);
    const [result, setResult] = useState(null);
    const [applyError, setApplyError] = useState(null);

    const searchTimerRef = useRef(null);

    // ── Load service types ──
    useEffect(() => {
        const load = async () => {
            try {
                const types = await listServiceTypes();
                setServiceTypes(types);
                const init = {};
                types.forEach(t => { init[t.id] = { pricePerHour: '', additionalPrice: '' }; });
                setPricing(init);
            } catch (e) {
                console.error('Failed to load service types:', e);
            } finally {
                setLoadingST(false);
            }
        };
        load();
    }, []);

    // ── Service type selection ──
    const allSTSelected = serviceTypes.length > 0 && selectedSTIds.size === serviceTypes.length;
    const someSTSelected = selectedSTIds.size > 0 && !allSTSelected;

    const toggleAllST = () => {
        if (allSTSelected) setSelectedSTIds(new Set());
        else setSelectedSTIds(new Set(serviceTypes.map(t => t.id)));
    };

    const toggleST = (id) => {
        setSelectedSTIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const updatePricingField = (id, field, value) => {
        setPricing(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
    };

    const applyQuickFill = () => {
        if (!quickFse && !quickInvoice) return;
        const targets = selectedSTIds.size > 0 ? selectedSTIds : new Set(serviceTypes.map(t => t.id));
        setPricing(prev => {
            const next = { ...prev };
            targets.forEach(id => {
                next[id] = {
                    pricePerHour: quickFse !== '' ? quickFse : prev[id]?.pricePerHour ?? '',
                    additionalPrice: quickInvoice !== '' ? quickInvoice : prev[id]?.additionalPrice ?? '',
                };
            });
            return next;
        });
    };

    const computeGst = (additionalPrice) => {
        const v = parseFloat(additionalPrice);
        if (isNaN(v) || v <= 0) return '—';
        return `$${(Math.round(v * (1 + GST_RATE) * 100) / 100).toFixed(2)}`;
    };

    // Build the final pricing map (only checked rows with ≥1 price set)
    const buildPricingMap = useCallback(() => {
        const map = {};
        for (const t of serviceTypes) {
            if (!selectedSTIds.has(t.id)) continue;
            const row = pricing[t.id] || {};
            const fse = parseFloat(row.pricePerHour);
            const inv = parseFloat(row.additionalPrice);
            if (isNaN(fse) && isNaN(inv)) continue;
            map[t.id] = {
                name: t.name,
                pricePerHour: !isNaN(fse) ? fse : null,
                additionalPrice: !isNaN(inv) ? inv : null,
                invoicePricePerHour: !isNaN(inv) ? Math.round(inv * (1 + GST_RATE) * 100) / 100 : null,
            };
        }
        return map;
    }, [serviceTypes, selectedSTIds, pricing]);

    const step1Valid = () => Object.keys(buildPricingMap()).length > 0;
    const step2Valid = () => supplierMode === 'all' || selectedClients.length > 0;

    // ── Client search (debounced) ──
    const handleClientSearch = (value) => {
        setClientSearchTerm(value);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        if (value.trim().length < 2) { setClientSuggestions([]); return; }
        setIsSearchingClients(true);
        searchTimerRef.current = setTimeout(async () => {
            try {
                const data = await searchClients({ searchString: value, pageSize: 10 });
                setClientSuggestions(data.clients || []);
            } catch {
                setClientSuggestions([]);
            } finally {
                setIsSearchingClients(false);
            }
        }, 300);
    };

    const addClientToSelection = (client) => {
        if (!selectedClients.find(c => c.id === client.id)) {
            setSelectedClients(prev => [...prev, { id: client.id, clientName: client.clientName }]);
        }
        setClientSearchTerm('');
        setClientSuggestions([]);
    };

    const removeClientFromSelection = (id) => setSelectedClients(prev => prev.filter(c => c.id !== id));

    // ── Apply action ──
    const handleApply = async () => {
        setShowConfirm(false);
        setApplying(true);
        setApplyError(null);
        setResult(null);
        try {
            const pricingMap = buildPricingMap();
            const clientIds = supplierMode === 'all' ? null : selectedClients.map(c => c.id);
            const response = await applyServicePricingBulk(clientIds, pricingMap);
            setResult(response.data);
        } catch (err) {
            setApplyError(err.message || 'An error occurred during the bulk price update.');
        } finally {
            setApplying(false);
        }
    };

    // Derived for review step
    const pricingMap = buildPricingMap();
    const pricingEntries = Object.entries(pricingMap);

    // ─── Render ──────────────────────────────────────────────────────────────
    return (
        <div className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">

            {/* Header */}
            <div className="mb-6 flex items-center justify-between px-4 sm:px-0">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Bulk Price Update</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Set service prices and push them across multiple suppliers at once.</p>
                </div>
                <button
                    onClick={onBack}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                    ← Back to Utilities
                </button>
            </div>

            <div className="bg-white shadow sm:rounded-lg px-6 py-6">
                <StepIndicator step={step} complete={step === 3 && !!result} />

                {/* ─── STEP 1: Set Prices ─── */}
                {step === 1 && (
                    <div>
                        <h2 className="text-base font-semibold text-gray-800 mb-1">Set Prices</h2>
                        <p className="text-sm text-gray-500 mb-5">
                            Check the service types you want to update and enter the new prices.
                            Only checked rows with at least one price will be included.
                        </p>

                        {loadingST ? (
                            <div className="py-12 text-center text-gray-400">Loading service types...</div>
                        ) : (
                            <>
                                {/* Quick Fill Bar */}
                                <div className="px-4 py-3 bg-blue-50 border border-blue-100 rounded-lg mb-4">
                                    <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
                                        Quick Fill{' '}
                                        <span className="font-normal normal-case text-blue-500">
                                            {selectedSTIds.size > 0 ? `(${selectedSTIds.size} selected)` : '(all rows)'}
                                        </span>
                                    </p>
                                    <div className="flex flex-wrap gap-3 items-end">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">FSE Rate ($/hr)</label>
                                            <input
                                                type="number" step="0.01" min="0"
                                                value={quickFse}
                                                onChange={e => setQuickFse(e.target.value)}
                                                className="w-32 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                                                placeholder="e.g. 80" />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Invoice ex. GST ($/hr)</label>
                                            <input
                                                type="number" step="0.01" min="0"
                                                value={quickInvoice}
                                                onChange={e => setQuickInvoice(e.target.value)}
                                                className="w-36 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                                                placeholder="e.g. 82" />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={applyQuickFill}
                                            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors">
                                            Apply to {selectedSTIds.size > 0 ? 'Selected' : 'All'}
                                        </button>
                                    </div>
                                </div>

                                {/* Service Type Table */}
                                <div className="border border-gray-200 rounded-lg overflow-hidden">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50">
                                            <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                <th className="px-4 py-3 w-8">
                                                    <button type="button" onClick={toggleAllST} className="text-gray-400 hover:text-gray-600">
                                                        {allSTSelected
                                                            ? <FaCheckSquare size={16} className="text-blue-500" />
                                                            : someSTSelected
                                                                ? <FaCheckSquare size={16} className="text-blue-300" />
                                                                : <FaRegSquare size={16} />}
                                                    </button>
                                                </th>
                                                <th className="px-4 py-3">Service Type</th>
                                                <th className="px-4 py-3 w-36">FSE Rate ($/hr)</th>
                                                <th className="px-4 py-3 w-40">Invoice ex. GST ($/hr)</th>
                                                <th className="px-4 py-3 w-36 text-right">Invoice w/ GST</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {serviceTypes.map(t => {
                                                const row = pricing[t.id] || {};
                                                const isSelected = selectedSTIds.has(t.id);
                                                return (
                                                    <tr key={t.id} className={isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                                                        <td className="px-4 py-2.5">
                                                            <button type="button" onClick={() => toggleST(t.id)} className="text-gray-400 hover:text-gray-600">
                                                                {isSelected
                                                                    ? <FaCheckSquare size={16} className="text-blue-500" />
                                                                    : <FaRegSquare size={16} />}
                                                            </button>
                                                        </td>
                                                        <td className="px-4 py-2.5 font-medium text-gray-800">{t.name}</td>
                                                        <td className="px-4 py-2.5">
                                                            <div className="relative">
                                                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                                                                <input
                                                                    type="number" step="0.01" min="0"
                                                                    value={row.pricePerHour || ''}
                                                                    onChange={e => updatePricingField(t.id, 'pricePerHour', e.target.value)}
                                                                    className="w-full pl-5 pr-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-jims-cyan"
                                                                    placeholder="—" />
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-2.5">
                                                            <div className="relative">
                                                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                                                                <input
                                                                    type="number" step="0.01" min="0"
                                                                    value={row.additionalPrice || ''}
                                                                    onChange={e => updatePricingField(t.id, 'additionalPrice', e.target.value)}
                                                                    className="w-full pl-5 pr-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-jims-cyan"
                                                                    placeholder="—" />
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-2.5 text-right text-gray-500">
                                                            {computeGst(row.additionalPrice)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {selectedSTIds.size > 0 && (
                                    <p className="text-xs text-gray-500 mt-2">
                                        {Object.keys(buildPricingMap()).length} of {selectedSTIds.size} checked service type(s) have prices set and will be included.
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* ─── STEP 2: Select Suppliers ─── */}
                {step === 2 && (
                    <div>
                        <h2 className="text-base font-semibold text-gray-800 mb-1">Select Suppliers</h2>
                        <p className="text-sm text-gray-500 mb-5">Choose which suppliers to update.</p>

                        {/* Mode selection */}
                        <div className="space-y-3 mb-5">
                            <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors
                                ${supplierMode === 'all' ? 'border-jims-green bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}>
                                <input
                                    type="radio" name="supplierMode" value="all"
                                    checked={supplierMode === 'all'}
                                    onChange={() => setSupplierMode('all')}
                                    className="mt-0.5 text-jims-green" />
                                <div>
                                    <span className="font-medium text-gray-900">All Suppliers</span>
                                    <p className="text-sm text-gray-500 mt-0.5">Apply prices to every supplier in the system.</p>
                                </div>
                            </label>

                            <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors
                                ${supplierMode === 'select' ? 'border-jims-green bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}>
                                <input
                                    type="radio" name="supplierMode" value="select"
                                    checked={supplierMode === 'select'}
                                    onChange={() => setSupplierMode('select')}
                                    className="mt-0.5 text-jims-green" />
                                <div className="flex-1">
                                    <span className="font-medium text-gray-900">Select Specific Suppliers</span>
                                    <p className="text-sm text-gray-500 mt-0.5">Search and choose individual suppliers.</p>
                                </div>
                            </label>
                        </div>

                        {/* Supplier search (only when 'select' mode) */}
                        {supplierMode === 'select' && (
                            <div>
                                {/* Selected chips */}
                                {selectedClients.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {selectedClients.map(c => (
                                            <span key={c.id} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                                                {c.clientName}
                                                <button
                                                    type="button"
                                                    onClick={() => removeClientFromSelection(c.id)}
                                                    className="text-blue-500 hover:text-blue-700 ml-1">
                                                    <FaTimes size={10} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Search input */}
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        {isSearchingClients
                                            ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-jims-green" />
                                            : <FaSearch className="h-4 w-4 text-gray-400" />}
                                    </div>
                                    <input
                                        type="text"
                                        className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-jims-green"
                                        placeholder="Search suppliers by name..."
                                        value={clientSearchTerm}
                                        onChange={e => handleClientSearch(e.target.value)} />
                                    {clientSearchTerm && (
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                            onClick={() => { setClientSearchTerm(''); setClientSuggestions([]); }}>
                                            <FaTimes className="h-4 w-4" />
                                        </button>
                                    )}

                                    {/* Suggestions dropdown */}
                                    {clientSuggestions.length > 0 && (
                                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto">
                                            {clientSuggestions.map(c => {
                                                const alreadyAdded = selectedClients.some(sc => sc.id === c.id);
                                                return (
                                                    <button
                                                        key={c.id}
                                                        type="button"
                                                        disabled={alreadyAdded}
                                                        onClick={() => addClientToSelection(c)}
                                                        className={`w-full text-left px-4 py-2.5 text-sm border-b border-gray-100 last:border-0 transition-colors
                                                            ${alreadyAdded
                                                                ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                                                                : 'hover:bg-gray-50 text-gray-800'}`}>
                                                        {c.clientName}
                                                        {alreadyAdded && <span className="ml-2 text-xs text-gray-400">(already added)</span>}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {selectedClients.length === 0 && (
                                    <p className="text-xs text-amber-600 mt-2">Search and add at least one supplier to continue.</p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ─── STEP 3: Review & Apply ─── */}
                {step === 3 && (
                    <div>
                        <h2 className="text-base font-semibold text-gray-800 mb-1">Review & Apply</h2>
                        <p className="text-sm text-gray-500 mb-5">Confirm the details below before applying.</p>

                        {/* Summary card */}
                        <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm space-y-1">
                            <p className="text-gray-700">
                                <span className="font-medium">Service types to update:</span>{' '}
                                <span className="text-gray-900">{pricingEntries.length}</span>
                            </p>
                            <p className="text-gray-700">
                                <span className="font-medium">Suppliers:</span>{' '}
                                <span className="text-gray-900">
                                    {supplierMode === 'all' ? 'All suppliers' : `${selectedClients.length} selected`}
                                </span>
                            </p>
                        </div>

                        {/* Price review table */}
                        <div className="border border-gray-200 rounded-lg overflow-hidden mb-5">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50">
                                    <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <th className="px-4 py-3">Service Type</th>
                                        <th className="px-4 py-3 w-36">FSE Rate ($/hr)</th>
                                        <th className="px-4 py-3 w-40">Invoice ex. GST</th>
                                        <th className="px-4 py-3 w-36 text-right">Invoice w/ GST</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {pricingEntries.map(([id, entry]) => (
                                        <tr key={id} className="hover:bg-gray-50">
                                            <td className="px-4 py-2.5 font-medium text-gray-800">{entry.name}</td>
                                            <td className="px-4 py-2.5 text-gray-700">
                                                {entry.pricePerHour != null ? `$${entry.pricePerHour}` : '—'}
                                            </td>
                                            <td className="px-4 py-2.5 text-gray-700">
                                                {entry.additionalPrice != null ? `$${entry.additionalPrice}` : '—'}
                                            </td>
                                            <td className="px-4 py-2.5 text-right text-gray-700">
                                                {entry.invoicePricePerHour != null ? `$${entry.invoicePricePerHour.toFixed(2)}` : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Selected suppliers list */}
                        {supplierMode === 'select' && selectedClients.length > 0 && (
                            <div className="mb-5">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                    Suppliers to update
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {selectedClients.map(c => (
                                        <span key={c.id} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                                            {c.clientName}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Warning */}
                        <div className="flex gap-2 items-start p-3 bg-amber-50 border border-amber-200 rounded-lg mb-5 text-sm text-amber-800">
                            <FaExclamationTriangle className="mt-0.5 flex-shrink-0" />
                            <span>
                                This will overwrite existing prices on all matching <strong>active</strong> job schedules
                                and their non-terminal jobs. Schedules with multiple service types will be skipped.
                            </span>
                        </div>

                        {/* Error */}
                        {applyError && (
                            <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-400 text-sm text-red-700">
                                {applyError}
                            </div>
                        )}

                        {/* Result */}
                        {result && (
                            <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    <FaCheckCircle className="text-green-500" />
                                    <span className="font-semibold text-green-800">Update Complete</span>
                                </div>
                                <ul className="text-sm text-green-700 space-y-0.5 ml-6 list-disc">
                                    <li>Suppliers processed: <strong>{result.clientsProcessed}</strong></li>
                                    <li>Schedules updated: <strong>{result.schedulesUpdated}</strong></li>
                                    <li>Jobs updated: <strong>{result.jobsUpdated}</strong></li>
                                    {result.skipped?.length > 0 && (
                                        <li className="text-amber-700">
                                            Skipped (multiple service types): <strong>{result.skipped.length}</strong>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        )}

                        {!result && (
                            <div className="flex flex-col items-start gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowConfirm(true)}
                                    disabled={applying}
                                    className="flex items-center gap-2 px-5 py-2 bg-jims-green text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                    {applying && (
                                        <svg className="animate-spin h-4 w-4 text-white flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                    )}
                                    {applying ? 'Applying...' : 'Apply Prices'}
                                </button>
                                {applying && (
                                    <p className="text-xs text-gray-500 italic">
                                        Large updates across many suppliers can take several minutes. Please keep this page open and do not navigate away until it completes.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ─── Footer navigation ─── */}
                <div className="mt-8 pt-4 border-t border-gray-200 flex justify-between items-center">
                    <button
                        type="button"
                        onClick={() => step > 1 ? setStep(s => s - 1) : onBack()}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">
                        <FaArrowLeft size={12} />
                        {step > 1 ? 'Back' : 'Utilities'}
                    </button>

                    {step < 3 && (
                        <button
                            type="button"
                            onClick={() => setStep(s => s + 1)}
                            disabled={(step === 1 && !step1Valid()) || (step === 2 && !step2Valid())}
                            className="flex items-center gap-2 px-5 py-2 bg-jims-green text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                            Next
                            <FaArrowRight size={12} />
                        </button>
                    )}
                </div>
            </div>

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showConfirm}
                onClose={() => setShowConfirm(false)}
                onConfirm={handleApply}
                title="Confirm Bulk Price Update"
                message={
                    <div className="text-left">
                        <div className="flex items-center gap-2 mb-4 text-amber-600">
                            <FaExclamationTriangle className="h-5 w-5 flex-shrink-0" />
                            <span className="font-semibold text-gray-900">Please review before proceeding</span>
                        </div>

                        {/* Service types */}
                        <div className="mb-4">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                Service Types ({pricingEntries.length})
                            </p>
                            <div className="bg-gray-50 border border-gray-200 rounded-md divide-y divide-gray-100 max-h-36 overflow-y-auto">
                                {pricingEntries.map(([id, entry]) => (
                                    <div key={id} className="flex justify-between items-center px-3 py-1.5 text-sm">
                                        <span className="font-medium text-gray-800">{entry.name}</span>
                                        <span className="text-gray-500 text-xs">
                                            FSE {entry.pricePerHour != null ? `$${entry.pricePerHour}` : '—'}
                                            {' · '}
                                            Inv {entry.additionalPrice != null ? `$${entry.additionalPrice}` : '—'}
                                            {' '}
                                            <span className="text-gray-400">
                                                (w/ GST {entry.invoicePricePerHour != null ? `$${entry.invoicePricePerHour.toFixed(2)}` : '—'})
                                            </span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Suppliers */}
                        <div className="mb-4">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                Suppliers
                            </p>
                            {supplierMode === 'all' ? (
                                <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-sm font-medium text-amber-800">
                                    ALL suppliers in the system
                                </div>
                            ) : (
                                <div className="bg-gray-50 border border-gray-200 rounded-md divide-y divide-gray-100 max-h-36 overflow-y-auto">
                                    {selectedClients.map(c => (
                                        <div key={c.id} className="px-3 py-1.5 text-sm text-gray-800">{c.clientName}</div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <p className="text-xs text-gray-500">
                            This will overwrite existing prices on matching active schedules and jobs. This cannot be undone.
                        </p>
                    </div>
                }
                isLoading={applying}
                loadingText="Applying prices..."
                confirmText="Yes, Apply Prices"
                variant="warning"
            />
        </div>
    );
};

export default MassPriceUpdateUtility;
